import React, { useState, useEffect, useCallback } from "react";
import { Star, Loader2 } from "lucide-react";
import api from "../lib/api";

const Stars = ({ value, size = 16, onChange }) => (
  <span className={`stars ${onChange ? "interactive" : ""}`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={size}
        onClick={onChange ? () => onChange(n) : undefined}
        className={n <= value ? "star on" : "star"}
        fill={n <= value ? "#f5a623" : "none"}
        stroke={n <= value ? "#f5a623" : "#c7d1dc"}
      />
    ))}
  </span>
);

const emptyForm = { author_name: "", author_role: "shipper", subject_name: "", subject_role: "driver", rating: 5, comment: "" };

const Reviews = () => {
  const [data, setData] = useState({ reviews: [], stats: { driver: { count: 0, average: 0 }, shipper: { count: 0, average: 0 } } });
  const [filter, setFilter] = useState("driver");
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/reviews");
      setData(r.data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reviews", form);
      setForm(emptyForm);
      setDone(true);
      load();
      setTimeout(() => setDone(false), 4000);
    } catch (err) { /* noop */ }
    finally { setBusy(false); }
  };

  // When author is a shipper, they review a driver, and vice-versa
  const onAuthorRole = (role) => {
    setForm({ ...form, author_role: role, subject_role: role === "shipper" ? "driver" : "shipper" });
  };

  const shown = data.reviews.filter((r) => r.subject_role === filter);

  return (
    <section className="reviews" id="reviews">
      <div className="container">
        <div className="section-head">
          <span className="kicker">Ratings &amp; Reviews</span>
          <h2>A two-way street of trust</h2>
          <p>Shippers rate drivers and drivers rate shippers — after every completed job — so everyone stays on their best behavior.</p>
        </div>

        <div className="rating-stats">
          <button className={`rating-stat ${filter === "driver" ? "on" : ""}`} onClick={() => setFilter("driver")}>
            <div className="rating-num">{data.stats.driver.average || "–"}</div>
            <Stars value={Math.round(data.stats.driver.average)} size={18} />
            <div className="rating-label">Driver rating · {data.stats.driver.count} reviews</div>
          </button>
          <button className={`rating-stat ${filter === "shipper" ? "on" : ""}`} onClick={() => setFilter("shipper")}>
            <div className="rating-num">{data.stats.shipper.average || "–"}</div>
            <Stars value={Math.round(data.stats.shipper.average)} size={18} />
            <div className="rating-label">Shipper rating · {data.stats.shipper.count} reviews</div>
          </button>
        </div>

        <div className="reviews-grid">
          <div className="review-list">
            {shown.length === 0 && <div className="review-empty">No {filter} reviews yet. Be the first to leave one!</div>}
            {shown.map((r) => (
              <div className="review-card" key={r.id}>
                <div className="review-top">
                  <div>
                    <div className="review-subject">{r.subject_name}</div>
                    <div className="review-author">by {r.author_name} · <span className="role-pill">{r.author_role}</span></div>
                  </div>
                  <Stars value={r.rating} />
                </div>
                {r.comment && <p className="review-comment">“{r.comment}”</p>}
              </div>
            ))}
          </div>

          <form className="review-form" onSubmit={submit}>
            <h3>Leave a review</h3>
            {done && <div className="review-done">Thanks! Your review was posted.</div>}
            <div className="field">
              <label>I am a</label>
              <div className="role-toggle">
                <button type="button" className={form.author_role === "shipper" ? "on" : ""} onClick={() => onAuthorRole("shipper")}>Shipper</button>
                <button type="button" className={form.author_role === "driver" ? "on" : ""} onClick={() => onAuthorRole("driver")}>Driver</button>
              </div>
            </div>
            <div className="field">
              <label>Your name / company</label>
              <input required value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} placeholder="Acme Shipping" />
            </div>
            <div className="field">
              <label>{form.author_role === "shipper" ? "Driver" : "Shipper"} you're reviewing</label>
              <input required value={form.subject_name} onChange={(e) => setForm({ ...form, subject_name: e.target.value })} placeholder={form.author_role === "shipper" ? "Driver name" : "Shipper / broker name"} />
            </div>
            <div className="field">
              <label>Rating</label>
              <Stars value={form.rating} size={26} onChange={(n) => setForm({ ...form, rating: n })} />
            </div>
            <div className="field">
              <label>Comment</label>
              <textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="How did the job go?"></textarea>
            </div>
            <button className="btn btn-primary btn-block" disabled={busy}>{busy ? <Loader2 size={16} className="spin" /> : null} Post Review</button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
